import React, { useEffect, useMemo, useState } from 'react';
import {
  Users, Star, MapPin, ClipboardCheck, ShieldCheck, Building2, CheckCircle2, XCircle,
  RefreshCw, Plus, Search, Trash2, Archive, User, ChevronRight, AlertTriangle, Sparkles,
  DollarSign,
} from 'lucide-react';
import { apiCall } from '../../utils/api';
import { toast } from '../ui/sonner';
import RosterVerificationControl from '../org/RosterVerificationControl';
import BulkAssignPanel from '../org/BulkAssignPanel';
import ShiftTemplatesTab from '../org/ShiftTemplatesTab';
import AttendanceRescuePanel from '../org/AttendanceRescuePanel';
import AIWorkforcePanel from '../org/AIWorkforcePanel';
import { ReportsComplianceTab } from '../org/ReportsCompliancePanel';
import StreakRewardsPanel from '../org/StreakRewardsPanel';
import SettlementScheduleCard from '../org/SettlementScheduleCard';
import AgencyPayAnalytics from './AgencyPayAnalytics';
import AgencyCompliancePanel from '../hris/AgencyCompliancePanel';

/**
 * AgentPortalPanel
 * =================
 * One-stop UI for every endpoint added in `agent_portal_routes.py`.
 * Tabs (top-level):
 *
 *   1. Programs — CRUD + archive scoped to the agent's own agency
 *   2. Locations — same, on the agent_locations collection
 *   3. Directory — searchable worker index with a "star" toggle for
 *      Preferred and a card-level snapshot of each worker
 *   4. Preferred — curated shortlist with notes and quick-remove
 *   5. Verifications — pending credential queue with approve / reject /
 *      resubmit actions
 *   6. Approval Center — dashboard-style summary of everything awaiting
 *      review
 *
 * Every mutation triggers a targeted refetch of the affected list so
 * the UI stays real-time without page reload.
 */
const TABS = [
  { id: 'approvals',    label: 'Approvals',     icon: ClipboardCheck },
  { id: 'programs',     label: 'Programs',      icon: Building2 },
  { id: 'locations',    label: 'Locations',     icon: MapPin },
  { id: 'directory',    label: 'Directory',     icon: Users },
  { id: 'bulk-assign',  label: 'Bulk Assign',   icon: ClipboardCheck },
  { id: 'preferred',    label: 'Preferred',     icon: Star },
  { id: 'verifications',label: 'Verifications', icon: ShieldCheck },
  { id: 'templates',    label: 'Templates',     icon: ClipboardCheck },
  { id: 'attendance',   label: 'Attendance',    icon: ShieldCheck },
  { id: 'ai',           label: 'AI Workforce',  icon: Sparkles },
  { id: 'rewards',      label: 'Streak Rewards', icon: Star },
  { id: 'payouts',      label: 'Payouts',       icon: ClipboardCheck },
  { id: 'pay-analytics',label: 'Pay Analytics', icon: DollarSign },
  { id: 'compliance',   label: 'Compliance',    icon: ShieldCheck },
  { id: 'reports',      label: 'Reports & Compliance', icon: ShieldCheck },
];

export default function AgentPortalPanel() {
  const [tab, setTab] = useState('approvals');
  const [agency, setAgency] = useState(null);
  const [checkedOrg, setCheckedOrg] = useState(false);

  useEffect(() => { (async () => {
    try {
      // UAT (Feb 2026): the agent no longer needs to explicitly set up
      // an organization — the backend auto-provisions one on demand.
      // We still fetch the agency record for the header display but
      // never block the tabs on it.
      const r = await apiCall('/api/agent/organization');
      setAgency(r.agency);
    } catch (_e) { /* silent */ } finally { setCheckedOrg(true); }
  })(); }, []);

  if (!checkedOrg) return <div className="p-8 text-slate-500 text-sm">Loading agency…</div>;

  // Onboarding gate removed — even without a persisted agency we render
  // the tabs. Downstream endpoints auto-create a stub agency the first
  // time they're called.
  const displayAgency = agency || { name: 'My Agency', verification_status: 'approved', onboarding_complete: true };

  return (
    <div className="space-y-4" data-testid="agent-portal-panel">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-gradient-to-br from-indigo-500 to-fuchsia-500 rounded-xl">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-900">{displayAgency.name}</h2>
            <p className="text-xs text-slate-500">
              {displayAgency.verification_status === 'approved'
                ? 'Verified agency'
                : `Verification ${displayAgency.verification_status || 'pending'}`}
              {' · '}
              {displayAgency.onboarding_complete ? 'Onboarding complete' : 'Onboarding in progress'}
            </p>
          </div>
        </div>
      </div>

      <div className="flex gap-1 overflow-x-auto border-b border-slate-200">
        {TABS.map((t) => {
          const Icon = t.icon;
          const isActive = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition ${
                isActive
                  ? 'border-indigo-600 text-indigo-700'
                  : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
              data-testid={`agent-portal-tab-${t.id}`}
            >
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      <div className="pt-2">
        {tab === 'approvals'     && <ApprovalsTab />}
        {tab === 'programs'      && <ProgramsTab />}
        {tab === 'locations'     && <AgentLocationsTab />}
        {tab === 'directory'     && <DirectoryTab />}
        {tab === 'bulk-assign'   && <BulkAssignPanel workersEndpoint="/api/agent/workers" />}
        {tab === 'preferred'     && <PreferredTab />}
        {tab === 'verifications' && <VerificationsTab />}
        {tab === 'templates'     && <ShiftTemplatesTab />}
        {tab === 'attendance'    && <AttendanceRescuePanel />}
        {tab === 'ai'            && <AIWorkforcePanel />}
        {tab === 'rewards'       && <StreakRewardsPanel role="agent" />}
        {tab === 'payouts'       && <SettlementScheduleCard />}
        {tab === 'pay-analytics' && <AgencyPayAnalytics />}
        {tab === 'compliance'    && <AgencyCompliancePanel />}
        {tab === 'reports'       && <ReportsComplianceTab />}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// Approval Center + Dashboard snapshot
// ---------------------------------------------------------------------
function ApprovalsTab() {
  const [summary, setSummary] = useState({ pending_credentials: 0, pending_worker_applications: 0, pending_shift_requests: 0, total: 0 });
  const [widgets, setWidgets] = useState({});
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [a, d] = await Promise.all([apiCall('/api/agent/approvals/center'), apiCall('/api/agent/dashboard')]);
      setSummary(a.summary || {});
      setWidgets(d.widgets || {});
    } catch (e) { toast.error(e.message || 'Failed to load approvals'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-slate-900">Approval Center</h3>
        <button onClick={load} className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Total pending" value={summary.total} accent icon={ClipboardCheck} />
        <Kpi label="Credentials" value={summary.pending_credentials} icon={ShieldCheck} />
        <Kpi label="Worker apps"  value={summary.pending_worker_applications} icon={Users} />
        <Kpi label="Shift requests" value={summary.pending_shift_requests} icon={CheckCircle2} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Active workers"      value={widgets.workers || 0}       icon={Users} />
        <Kpi label="Available now"       value={widgets.available_workers || 0} icon={Users} />
        <Kpi label="Preferred"           value={widgets.preferred_workers || 0} icon={Star} />
        <Kpi label="Open jobs"           value={widgets.open_jobs || 0}      icon={ClipboardCheck} />
        <Kpi label="Programs"            value={widgets.programs || 0}       icon={Building2} />
        <Kpi label="Locations"           value={widgets.locations || 0}      icon={MapPin} />
        <Kpi label="Expiring credentials" value={widgets.expiring_credentials || 0} icon={AlertTriangle} />
        <Kpi label="Unread messages"     value={widgets.messages || 0}       icon={ChevronRight} />
      </div>
      {loading && <p className="text-xs text-slate-500">Refreshing…</p>}
    </div>
  );
}

function Kpi({ label, value, accent = false, icon: Icon }) {
  return (
    <div className={`rounded-xl p-3 border ${accent ? 'bg-gradient-to-br from-indigo-600 to-fuchsia-600 text-white border-transparent' : 'bg-white border-slate-200'}`}>
      <div className={`flex items-center gap-2 text-xs uppercase tracking-wide ${accent ? 'text-white/80' : 'text-slate-500'}`}>
        {Icon && <Icon className="w-3.5 h-3.5" />} {label}
      </div>
      <p className={`mt-1 text-lg font-semibold ${accent ? 'text-white' : 'text-slate-900'}`}>{value}</p>
    </div>
  );
}

// ---------------------------------------------------------------------
// Programs
// ---------------------------------------------------------------------
function ProgramsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setItems((await apiCall('/api/agent/programs')).programs || []); }
    catch (e) { toast.error(e.message); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const archive = async (id) => {
    try { await apiCall(`/api/agent/programs/${id}/archive`, { method: 'POST' }); toast.success('Archived'); load(); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-slate-900">Programs / Group Homes</h3>
        <button onClick={() => setShowNew(true)} className="inline-flex items-center gap-1 text-sm px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                data-testid="agent-program-add">
          <Plus className="w-4 h-4" /> New program
        </button>
      </div>
      {loading ? <SkeletonRows /> : items.length === 0 ? <EmptyState label="No programs yet." /> :
        <ul className="divide-y divide-slate-100 bg-white border border-slate-200 rounded-xl">
          {items.map((p) => (
            <li key={p._id} className="flex items-center justify-between px-4 py-3">
              <div>
                <p className="font-medium text-slate-900">{p.name}</p>
                <p className="text-xs text-slate-500">{p.category || 'General'}{p.description ? ` · ${p.description.slice(0, 80)}` : ''}</p>
              </div>
              <button onClick={() => archive(p._id)} className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50" title="Archive">
                <Archive className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      }
      {showNew && (
        <QuickCreateModal
          title="New program"
          fields={[
            { key: 'name', label: 'Name', required: true },
            { key: 'category', label: 'Category' },
            { key: 'description', label: 'Description', textarea: true },
          ]}
          onSave={async (v) => { await apiCall('/api/agent/programs', { method: 'POST', body: JSON.stringify(v) }); toast.success('Program created'); setShowNew(false); load(); }}
          onCancel={() => setShowNew(false)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------
// Locations
// ---------------------------------------------------------------------
export function AgentLocationsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setItems((await apiCall('/api/agent/agency-locations')).locations || []); }
    catch (e) { toast.error(e.message); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const archive = async (id) => {
    try { await apiCall(`/api/agent/agency-locations/${id}/archive`, { method: 'POST' }); toast.success('Archived'); load(); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-slate-900">Locations</h3>
        <button onClick={() => setShowNew(true)} className="inline-flex items-center gap-1 text-sm px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                data-testid="agent-location-add">
          <Plus className="w-4 h-4" /> New location
        </button>
      </div>
      {loading ? <SkeletonRows /> : items.length === 0 ? <EmptyState label="No locations yet." /> :
        <ul className="divide-y divide-slate-100 bg-white border border-slate-200 rounded-xl">
          {items.map((l) => (
            <li key={l._id} className="flex items-center justify-between px-4 py-3">
              <div>
                <p className="font-medium text-slate-900">{l.name}</p>
                <p className="text-xs text-slate-500">{l.address}{l.city ? `, ${l.city}` : ''}{l.state ? `, ${l.state}` : ''}</p>
              </div>
              <button onClick={() => archive(l._id)} className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50" title="Archive">
                <Archive className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      }
      {showNew && (
        <QuickCreateModal
          title="New location"
          fields={[
            { key: 'name', label: 'Name', required: true },
            { key: 'address', label: 'Address', required: true },
            { key: 'city', label: 'City' },
            { key: 'state', label: 'State' },
            { key: 'zip', label: 'ZIP' },
          ]}
          onSave={async (v) => { await apiCall('/api/agent/agency-locations', { method: 'POST', body: JSON.stringify(v) }); toast.success('Location created'); setShowNew(false); load(); }}
          onCancel={() => setShowNew(false)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------
// Worker Directory
// ---------------------------------------------------------------------
function DirectoryTab() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [available, setAvailable] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const url = new URL('/api/agent/directory', window.location.origin);
      if (q) url.searchParams.set('q', q);
      if (available) url.searchParams.set('available_only', 'true');
      const path = url.pathname + url.search;
      setItems((await apiCall(path)).workers || []);
    } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const togglePreferred = async (w) => {
    try {
      if (w.preferred) {
        await apiCall(`/api/agent/preferred-workers/${w._id}`, { method: 'DELETE' });
      } else {
        await apiCall(`/api/agent/preferred-workers/${w._id}`, { method: 'POST', body: JSON.stringify({}) });
      }
      setItems((prev) => prev.map((x) => (x._id === w._id ? { ...x, preferred: !x.preferred } : x)));
      toast.success(w.preferred ? 'Removed from Preferred' : 'Added to Preferred');
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[16rem]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && load()}
            placeholder="Search by name, email, or skill…"
            className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm"
            data-testid="agent-directory-search"
          />
        </div>
        <label className="text-sm text-slate-600 flex items-center gap-2">
          <input type="checkbox" checked={available} onChange={(e) => setAvailable(e.target.checked)} />
          Available only
        </label>
        <button onClick={load} className="px-3 py-2 text-sm rounded-lg bg-indigo-600 text-white hover:bg-indigo-700">Search</button>
      </div>
      {loading ? <SkeletonRows /> : items.length === 0 ? <EmptyState label="No workers match." /> :
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {items.map((w) => (
            <div key={w._id} className="bg-white border border-slate-200 rounded-xl p-3 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-400 to-fuchsia-400 flex items-center justify-center text-white font-semibold">
                  {(w.full_name || w.email || '?').charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <p className="font-medium text-slate-900 truncate">{w.full_name || w.email}</p>
                  <p className="text-xs text-slate-500 truncate">
                    {(w.skills || []).slice(0, 2).join(', ') || '—'}
                    {w.availability_status ? ` · ${w.availability_status}` : ''}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <RosterVerificationControl workerId={w._id} workerName={w.full_name || w.email} />
                <button
                  onClick={() => togglePreferred(w)}
                  title={w.preferred ? 'Remove from preferred' : 'Add to preferred'}
                  className={`p-2 rounded-lg transition ${w.preferred ? 'text-amber-500 hover:bg-amber-50' : 'text-slate-400 hover:text-amber-500 hover:bg-slate-50'}`}
                  data-testid={`agent-directory-star-${w._id}`}
                >
                  <Star className="w-5 h-5" fill={w.preferred ? 'currentColor' : 'none'} />
                </button>
              </div>
            </div>
          ))}
        </div>
      }
    </div>
  );
}

// ---------------------------------------------------------------------
// Preferred workers
// ---------------------------------------------------------------------
function PreferredTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { setItems((await apiCall('/api/agent/preferred-workers')).preferred || []); }
    catch (e) { toast.error(e.message); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (workerId) => {
    try { await apiCall(`/api/agent/preferred-workers/${workerId}`, { method: 'DELETE' }); toast.success('Removed'); load(); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-4">
      <h3 className="font-semibold text-slate-900">Preferred Workers</h3>
      {loading ? <SkeletonRows /> : items.length === 0 ? <EmptyState label="No preferred workers yet. Star someone in the Directory to add them." /> :
        <ul className="divide-y divide-slate-100 bg-white border border-slate-200 rounded-xl">
          {items.map((p) => (
            <li key={p.id} className="flex items-center justify-between px-4 py-3">
              <div className="flex items-center gap-3 min-w-0">
                <User className="w-4 h-4 text-slate-400" />
                <div className="min-w-0">
                  <p className="font-medium text-slate-900 truncate">{p.worker_name || p.worker_email}</p>
                  <p className="text-xs text-slate-500 truncate">{p.notes || '—'}</p>
                </div>
              </div>
              <button onClick={() => remove(p.worker_id)} className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50" title="Remove">
                <Trash2 className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      }
    </div>
  );
}

// ---------------------------------------------------------------------
// Verifications
// ---------------------------------------------------------------------
function VerificationsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { setItems((await apiCall('/api/agent/verifications/pending')).pending || []); }
    catch (e) { toast.error(e.message); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const decide = async (workerId, index, decision, notes = '') => {
    try {
      await apiCall(`/api/agent/verifications/${workerId}/${index}/${decision}`, {
        method: 'POST', body: JSON.stringify({ notes }),
      });
      toast.success(`Credential ${decision.replace('_', ' ')}`);
      load();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-slate-900">Pending verifications</h3>
        <button onClick={load} className="text-xs text-slate-600 hover:text-slate-900 inline-flex items-center gap-1">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>
      {loading ? <SkeletonRows /> : items.length === 0 ? <EmptyState label="No credentials pending review." /> :
        <ul className="space-y-2">
          {items.map((c, i) => (
            <li key={`${c.worker_id}-${c.credential_index}-${i}`}
                className="bg-white border border-slate-200 rounded-xl p-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="font-medium text-slate-900 truncate">{c.credential_name || c.credential_type}</p>
                <p className="text-xs text-slate-500 truncate">
                  {c.worker_name} · type: {c.credential_type}
                  {c.expires_at ? ` · expires ${String(c.expires_at).slice(0, 10)}` : ''}
                </p>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => decide(c.worker_id, c.credential_index, 'approve')}
                        className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-lg" title="Approve"
                        data-testid={`verify-approve-${c.worker_id}-${c.credential_index}`}>
                  <CheckCircle2 className="w-5 h-5" />
                </button>
                <button onClick={() => decide(c.worker_id, c.credential_index, 'reject')}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg" title="Reject">
                  <XCircle className="w-5 h-5" />
                </button>
                <button onClick={() => decide(c.worker_id, c.credential_index, 'resubmit')}
                        className="p-1.5 text-amber-600 hover:bg-amber-50 rounded-lg" title="Request resubmission">
                  <RefreshCw className="w-5 h-5" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      }
    </div>
  );
}

// ---------------------------------------------------------------------
// Organization setup card + shared UI helpers
// ---------------------------------------------------------------------
function OrganizationSetupCard({ onCreated }) {
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [industry, setIndustry] = useState('');
  const [phone, setPhone] = useState('');
  const [website, setWebsite] = useState('');

  const save = async () => {
    if (!name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      const r = await apiCall('/api/agent/organization', {
        method: 'POST',
        body: JSON.stringify({ name, industry, phone, website, onboarding_complete: false }),
      });
      toast.success('Organization created');
      onCreated?.(r.agency);
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 max-w-xl mx-auto" data-testid="agent-org-setup">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-gradient-to-br from-indigo-500 to-fuchsia-500 rounded-xl">
          <Building2 className="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Set up your agency</h2>
          <p className="text-sm text-slate-500">Once your organization is created you can add programs, locations, and workers.</p>
        </div>
      </div>
      <div className="space-y-3">
        <Field label="Organization name" required value={name} onChange={setName} placeholder="Acme Staffing" />
        <Field label="Industry" value={industry} onChange={setIndustry} placeholder="healthcare" />
        <Field label="Phone" value={phone} onChange={setPhone} placeholder="555-123-4567" />
        <Field label="Website" value={website} onChange={setWebsite} placeholder="https://acmestaffing.com" />
      </div>
      <div className="mt-5 sticky bottom-0 bg-white pt-3 -mx-6 -mb-6 px-6 pb-6 border-t border-slate-100">
        <button onClick={save} disabled={saving}
                className="w-full py-2.5 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-50 font-medium"
                data-testid="agent-org-save">
          {saving ? 'Saving…' : 'Create organization'}
        </button>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, required, placeholder, textarea }) {
  const cls = 'w-full border border-slate-200 rounded-lg px-3 py-2 text-sm';
  return (
    <label className="block">
      <span className="text-xs font-medium text-slate-600">{label}{required && <span className="text-red-500 ml-1">*</span>}</span>
      {textarea ? (
        <textarea value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} rows={3} className={cls} />
      ) : (
        <input value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={cls} />
      )}
    </label>
  );
}

function QuickCreateModal({ title, fields, onSave, onCancel }) {
  const [state, setState] = useState({});
  const [saving, setSaving] = useState(false);
  const missing = fields.filter((f) => f.required && !state[f.key]);

  const save = async () => {
    if (missing.length) { toast.error(`Missing: ${missing.map((f) => f.label).join(', ')}`); return; }
    setSaving(true);
    try { await onSave(state); } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-semibold mb-4">{title}</h3>
        <div className="space-y-3">
          {fields.map((f) => (
            <Field key={f.key} label={f.label} required={f.required} textarea={f.textarea}
                   value={state[f.key] || ''} onChange={(v) => setState((s) => ({ ...s, [f.key]: v }))} />
          ))}
        </div>
        <div className="flex justify-end gap-2 mt-5 pt-3 border-t border-slate-100">
          <button onClick={onCancel} className="px-3 py-1.5 text-sm rounded-lg text-slate-600 hover:bg-slate-50">Cancel</button>
          <button onClick={save} disabled={saving}
                  className="px-4 py-1.5 text-sm rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function EmptyState({ label }) {
  return (
    <div className="p-8 text-center text-slate-500 text-sm border border-dashed border-slate-200 rounded-xl bg-slate-50">
      {label}
    </div>
  );
}

function SkeletonRows() {
  return (
    <div className="space-y-2">
      {[0, 1, 2].map((i) => (
        <div key={i} className="h-14 bg-slate-100 rounded-xl animate-pulse" />
      ))}
    </div>
  );
}
